import type { LocalDate } from "@js-joda/core";
import { TimeReportResolution, TimeReportStatusV2 } from "model/TimeReport";
import { isTimeReportAbsenceResolution } from "web-apps/company/utils/absenceLabels";
import {
  getStockholmLocalDateNow,
  parseIsoDateToLocalDateOrNull,
} from "web-apps/company/utils/timeAndDate";

export interface TimeReportActionPolicy {
  hasVisibleActions: boolean;
  showRetractAction: boolean;
  showAbsenceAction: boolean;
  showRejectAction: boolean;
  showApproveAction: boolean;
  showResetAction: boolean;
}

interface TimeReportFutureApprovalCandidate {
  readonly plannedStartTime: string | null;
  readonly reportedStartTime: string | null;
  readonly status: TimeReportStatusV2;
}

export function canEditTimeReport(
  status: TimeReportStatusV2 | null | undefined,
): boolean {
  return (
    status === TimeReportStatusV2.NEW ||
    status === TimeReportStatusV2.SUBMITTED ||
    status === TimeReportStatusV2.REJECTED
  );
}

export function isTimeReportScheduledInFuture(
  timeReportStart: string | null | undefined,
  today: LocalDate = getStockholmLocalDateNow(),
): boolean {
  const reportDate = parseIsoDateToLocalDateOrNull(timeReportStart);
  return reportDate != null && reportDate.isAfter(today);
}

export function isUnreportedFutureTimeReport(
  {
    status,
    plannedStartTime,
    reportedStartTime,
  }: TimeReportFutureApprovalCandidate,
  today: LocalDate = getStockholmLocalDateNow(),
): boolean {
  return (
    status === TimeReportStatusV2.NEW &&
    isTimeReportScheduledInFuture(plannedStartTime ?? reportedStartTime, today)
  );
}

/**
 * Resolves available time-report actions from status/resolution.
 * Shared by detail-panel actions and person-view bulk-action rules.
 */
export const resolveTimeReportActionPolicy = (
  status: TimeReportStatusV2 | null | undefined,
  resolution: TimeReportResolution | null | undefined,
): TimeReportActionPolicy => {
  const isUnsubmitted = status === TimeReportStatusV2.NEW;

  const isSubmitted = status === TimeReportStatusV2.SUBMITTED;

  const isClosed = status === TimeReportStatusV2.CLOSED;

  const isClosedTimeReported =
    isClosed && resolution === TimeReportResolution.TIME_REPORTED;

  const isClosedWithdrawn =
    isClosed && resolution === TimeReportResolution.WITHDRAWN;

  const isClosedAbsence =
    isClosed && resolution != null && isTimeReportAbsenceResolution(resolution);

  const isClosedOverdue =
    isClosed && resolution === TimeReportResolution.OVERDUE;

  const showRetractAction =
    isUnsubmitted || isSubmitted || isClosedTimeReported;

  const showAbsenceAction = isUnsubmitted || isSubmitted;

  const showRejectAction = isSubmitted;

  const showApproveAction = isUnsubmitted || isSubmitted;

  const showResetAction =
    isClosedTimeReported || isClosedWithdrawn || isClosedAbsence;

  const hasVisibleActions =
    !isClosedOverdue &&
    (showRetractAction ||
      showAbsenceAction ||
      showResetAction ||
      showRejectAction ||
      showApproveAction);

  return {
    hasVisibleActions,
    showRetractAction,
    showAbsenceAction,
    showRejectAction,
    showApproveAction,
    showResetAction,
  };
};
